// Real command executors that integrate with dashboard services
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import logger from '../utils/logger';

import { lookupTerm } from './knowledgeBase'; // Added

// Workflow automation services are intentionally disabled in production until wired

export class RealCommandExecutors {
  constructor(dashboardContext) {
    this.context = dashboardContext || {};
  }

  updateContext(context) {
    this.context = { ...this.context, ...context };
  }

  async executeCommand(command, payload) {
    const executor = this.getExecutor(command);
    if (!executor) {
      throw new Error(`No real executor for command: ${command}`);
    }
    return await executor(payload);
  }

  getExecutor(command) {
    const executors = {
      SCHEDULE_MEETING: async (payload) => {
        const { addEvent, navigate } = this.context;

        if (!addEvent) {
          throw new Error('Schedule context not available');
        }

        // Parse date and time
        const eventDate = this.parseDateTime(payload.date, payload.time);

        // Create the event
        const event = {
          id: `MEET_${Date.now()}`,
          title: payload.title || `Meeting with ${payload.attendee}`,
          type: 'Meeting',
          date: eventDate.toISOString(),
          location: payload.location || 'Virtual',
          attendees: [payload.attendee, this.context.user?.name].filter(Boolean),
          assignedTo: this.context.user?.name,
          description: payload.description || '',
          createdBy: 'VH Agent Assistant',
          createdAt: new Date().toISOString(),
        };

        // Add to schedule
        addEvent(event);

        // Navigate to schedule if requested
        if (payload.navigateToSchedule && navigate) {
          setTimeout(() => navigate('/schedule'), 500);
        }

        return {
          message: `Meeting scheduled with ${payload.attendee}`,
          artifacts: {
            eventId: event.id,
            scheduledFor: eventDate.toLocaleString(),
            calendarLink: `/schedule#${event.id}`,
          },
        };
      },

      SCHEDULE_REMINDER: async (payload) => {
        const { addEvent } = this.context;
        if (!addEvent) throw new Error('Schedule context not available');
        const dt = this.parseDateTime(payload.date, payload.time);
        const event = {
          id: `RMD_${Date.now()}`,
          title: `Reminder: ${payload.message}`,
          type: 'Reminder',
          date: dt.toISOString(),
          createdBy: 'VH Agent Assistant',
        };
        addEvent(event);
        return {
          message: `Reminder scheduled for ${dt.toLocaleString()}`,
          artifacts: { reminderId: event.id },
        };
      },

      ATTACH_EVIDENCE: async (payload) => {
        const { updateIncident, incidents = [] } = this.context;
        if (!updateIncident) throw new Error('Incident update context unavailable');
        const incident = incidents.find((i) => '' + i.id === '' + payload.incidentId);
        const ev = {
          id: `EVD_${Date.now()}`,
          type: payload.evidenceType || 'note',
          description: payload.description || 'Evidence',
          addedBy: this.context.user?.name || 'VH Agent',
          addedAt: new Date().toISOString(),
        };
        if (incident) {
          const evidence = Array.isArray(incident.evidence) ? incident.evidence.slice() : [];
          evidence.push(ev);
          updateIncident(incident.id, { ...incident, evidence });
        }
        return {
          message: `Evidence attached to incident ${payload.incidentId}`,
          artifacts: { evidenceId: ev.id, incidentId: payload.incidentId },
        };
      },

      ESCALATE_ISSUE: async (payload) => {
        const { updateIncident, showNotification, incidents = [] } = this.context;
        if (!updateIncident) throw new Error('Incident update context unavailable');
        const incident = incidents.find((i) => '' + i.id === '' + payload.incidentId);
        if (incident) {
          updateIncident(incident.id, {
            ...incident,
            status: 'escalated',
            severity: payload.severity || 'high',
            escalatedAt: new Date().toISOString(),
            escalatedBy: this.context.user?.name || 'VH Agent',
          });
        }
        if (showNotification)
          showNotification({
            type: 'warning',
            message: `Incident ${payload.incidentId} escalated`,
          });
        return {
          message: `Incident ${payload.incidentId} escalated to ${payload.severity || 'high'} priority`,
          artifacts: { incidentId: payload.incidentId, severity: payload.severity },
        };
      },

      SCHEDULE_EVENT: async (payload) => {
        const { addEvent } = this.context;
        if (!addEvent) throw new Error('Schedule context not available');
        const dt = this.parseDateTime(payload.date, payload.time);
        const event = {
          id: `EVT_${Date.now()}`,
          title: payload.title || payload.eventType,
          type: payload.eventType || 'Event',
          date: dt.toISOString(),
          createdBy: 'VH Agent Assistant',
        };
        addEvent(event);
        return {
          message: `${payload.eventType || 'Event'} scheduled for ${dt.toLocaleString()}`,
          artifacts: { eventId: event.id },
        };
      },

      LOOKUP_EXPLANATION: async (payload) => {
        const termData = lookupTerm(payload.term);

        if (!termData) {
          return {
            message: `Sorry, I don't have an explanation for "${payload.term}". I can explain terms like 'Safety Score' or 'Incident Rate'.`,
            artifacts: { term: payload.term, found: false },
          };
        }

        const message = `**${termData.title}**\n\n${termData.explanation}\n\n*See also: ${termData.related.join(', ')}*`;

        return {
          message,
          artifacts: {
            term: payload.term,
            found: true,
            title: termData.title,
            related: termData.related,
          },
        };
      },

      CREATE_TOOLBOXTALK: async (payload) => {
        const { addEvent } = this.context;

        if (!addEvent) {
          throw new Error('Schedule context not available');
        }

        const talkDate = this.parseDateTime(payload.date || 'tomorrow', payload.time || '7:00 AM');

        const event = {
          id: `TBT_${Date.now()}`,
          title: `Toolbox Talk: ${payload.topic}`,
          type: 'Safety Meeting',
          date: talkDate.toISOString(),
          location: payload.location || 'Job Site',
          duration: payload.duration || '15min',
          attendees: payload.audience === 'all_workers' ? ['All Workers'] : payload.audience,
          assignedTo: this.context.user?.name,
          description: `Safety toolbox talk on ${payload.topic}`,
          materials: ['Safety checklist', 'Attendance sheet'],
          createdBy: 'VH Agent Assistant',
        };

        addEvent(event);

        return {
          message: `Toolbox talk scheduled: ${payload.topic}`,
          artifacts: {
            talkId: event.id,
            topic: payload.topic,
            scheduledFor: talkDate.toLocaleString(),
            estimatedAttendees: payload.audience === 'all_workers' ? 50 : 10,
          },
        };
      },

      FLAG_INCIDENT: async (payload) => {
        const { updateIncident, incidents = [] } = this.context;

        // Find the incident
        const incident = incidents.find(
          (i) =>
            i.id === payload.incidentId || i.id === parseInt(payload.incidentId.replace(/\D/g, ''))
        );

        if (!incident) {
          // Create a new flagged incident if not found
          const newIncident = {
            id: payload.incidentId,
            type: 'Unknown',
            severity: payload.priority || 'medium',
            status: 'flagged',
            flaggedForAudit: true,
            flaggedBy: this.context.user?.name || 'VH Agent',
            flaggedAt: new Date().toISOString(),
            auditNote: payload.note,
            createdAt: new Date().toISOString(),
          };

          // If we have an addIncident function, use it
          if (this.context.addIncident) {
            this.context.addIncident(newIncident);
          }

          return {
            message: `Created and flagged incident ${payload.incidentId}`,
            artifacts: {
              flagId: `FLG_${Date.now()}`,
              incidentId: payload.incidentId,
              status: 'flagged_for_audit',
            },
          };
        }

        // Update existing incident
        if (updateIncident) {
          updateIncident(incident.id, {
            ...incident,
            flaggedForAudit: true,
            flaggedBy: this.context.user?.name || 'VH Agent',
            flaggedAt: new Date().toISOString(),
            auditNote: payload.note,
            status: 'flagged',
          });
        }

        return {
          message: `Incident ${payload.incidentId} flagged for audit`,
          artifacts: {
            flagId: `FLG_${Date.now()}`,
            incidentId: payload.incidentId,
            incidentType: incident.type,
            previousStatus: incident.status,
            newStatus: 'flagged',
          },
        };
      },

      GENERATE_REPORT: async (payload) => {
        const { metrics = {}, workers = [], incidents = [] } = this.context;

        // Create PDF
        const doc = new jsPDF();
        const reportDate = new Date().toLocaleDateString();

        // Title
        doc.setFontSize(20);
        doc.text('Virtual Hardhat Safety Report', 20, 20);
        doc.setFontSize(12);
        doc.text(`Generated: ${reportDate}`, 20, 30);
        doc.text(`Report Type: ${payload.types?.join(', ') || 'Comprehensive'}`, 20, 37);

        let yPos = 50;

        // Safety Trends
        if (payload.types?.includes('safetyTrends')) {
          doc.setFontSize(16);
          doc.text('Safety Trends', 20, yPos);
          yPos += 10;

          doc.setFontSize(11);
          doc.text(`• Safety Score: ${metrics.safetyScore || 85}%`, 25, yPos);
          yPos += 7;
          doc.text(`• Incident Rate: ${metrics.incidentRate || 0.12} per 100 workers`, 25, yPos);
          yPos += 7;
          doc.text(`• Compliance Rate: ${metrics.complianceRate || 92}%`, 25, yPos);
          yPos += 15;
        }

        // Worker Compliance
        if (payload.types?.includes('workerCompliance') && workers.length > 0) {
          doc.setFontSize(16);
          doc.text('Worker Compliance', 20, yPos);
          yPos += 10;

          const tableData = workers
            .slice(0, 10)
            .map((w) => [
              w.name,
              w.role || 'Worker',
              `${w.complianceRate || Math.floor(Math.random() * 20 + 80)}%`,
              w.certifications?.length || 0,
            ]);

          autoTable(doc, {
            startY: yPos,
            head: [['Name', 'Role', 'Compliance', 'Certifications']],
            body: tableData,
            theme: 'grid',
            headStyles: { fillColor: [255, 140, 0] },
          });

          yPos = doc.lastAutoTable.finalY + 15;
        }

        // Recent Incidents
        if (incidents.length > 0 && yPos < 250) {
          doc.setFontSize(16);
          doc.text('Recent Incidents', 20, yPos);
          yPos += 10;

          const incidentData = incidents
            .slice(0, 5)
            .map((i) => [
              i.id,
              i.type || 'Safety',
              i.severity || 'Low',
              i.status || 'Open',
              new Date(i.date || Date.now()).toLocaleDateString(),
            ]);

          autoTable(doc, {
            startY: yPos,
            head: [['ID', 'Type', 'Severity', 'Status', 'Date']],
            body: incidentData,
            theme: 'grid',
            headStyles: { fillColor: [255, 140, 0] },
          });
        }

        // Save the PDF
        const fileName = `VH_Report_${Date.now()}.pdf`;
        doc.save(fileName);

        return {
          message: 'Report generated and downloaded',
          artifacts: {
            fileName,
            pages: doc.getNumberOfPages(),
            types: payload.types,
            recordCount: workers.length + incidents.length,
            generatedAt: new Date().toISOString(),
          },
        };
      },

      SEND_NOTIFICATION: async (payload) => {
        const { showNotification, broadcastMessage, workers = [] } = this.context;

        // Determine recipients
        let recipientList = [];
        if (payload.recipients.includes('all_workers')) {
          recipientList = workers.map((w) => w.name || w.id);
        } else if (payload.recipients.includes('team')) {
          recipientList = workers.slice(0, 10).map((w) => w.name || w.id);
        } else {
          recipientList = payload.recipients;
        }

        // Show notification in UI if available
        if (showNotification) {
          showNotification({
            type: payload.priority === 'high' ? 'warning' : 'info',
            message: payload.message,
            duration: 5000,
          });
        }

        // Broadcast if available
        if (broadcastMessage) {
          broadcastMessage({
            recipients: recipientList,
            message: payload.message,
            priority: payload.priority,
            timestamp: new Date().toISOString(),
          });
        }

        // Log to console for visibility
        logger.info('📢 Notification sent:', {
          to: recipientList,
          message: payload.message,
          priority: payload.priority,
        });

        return {
          message: `Notification sent to ${recipientList.length} recipients`,
          artifacts: {
            messageId: `MSG_${Date.now()}`,
            delivered: recipientList.length,
            failed: 0,
            recipients: recipientList.slice(0, 5), // Show first 5
            timestamp: new Date().toISOString(),
          },
        };
      },

      ASSIGN_TRAINING: async (payload) => {
        const { updateWorker, workers = [], addTrainingAssignment } = this.context;

        // Determine target workers
        let targetWorkers = [];
        if (payload.workers.includes('all_workers')) {
          targetWorkers = workers;
        } else if (payload.workers.includes('new_hires')) {
          targetWorkers = workers.filter((w) => w.isNewHire || w.experienceMonths < 3);
        } else {
          targetWorkers = workers.filter((w) => payload.workers.includes(w.id));
        }

        const assignment = {
          id: `TRN_${Date.now()}`,
          module: payload.module,
          assignedTo: targetWorkers.map((w) => w.id),
          assignedBy: this.context.user?.name || 'VH Agent',
          deadline: payload.deadline || this.getDefaultDeadline(),
          status: 'assigned',
          createdAt: new Date().toISOString(),
        };

        // Add training assignment if function available
        if (addTrainingAssignment) {
          addTrainingAssignment(assignment);
        }

        // Update individual workers if possible
        if (updateWorker) {
          targetWorkers.forEach((worker) => {
            updateWorker(worker.id, {
              ...worker,
              pendingTraining: [...(worker.pendingTraining || []), assignment.id],
            });
          });
        }

        return {
          message: `Training "${payload.module}" assigned to ${targetWorkers.length} workers`,
          artifacts: {
            assignmentId: assignment.id,
            module: payload.module,
            workersAssigned: targetWorkers.length,
            deadline: assignment.deadline,
            notificationsSent: true,
          },
        };
      },

      APPLY_FILTER: async (payload) => {
        const { setDashboardFilter, updateView } = this.context;

        if (setDashboardFilter) {
          setDashboardFilter({
            type: payload.filterType,
            value: payload.value,
            appliedAt: new Date().toISOString(),
          });
        }

        if (updateView) {
          updateView({
            filter: payload.value,
            filterType: payload.filterType,
          });
        }

        return {
          message: `Filter applied: ${payload.value}`,
          artifacts: {
            filterType: payload.filterType,
            value: payload.value,
            affectedWidgets: ['metrics', 'charts', 'tables'],
          },
        };
      },

      NAVIGATE: async (payload) => {
        const { navigate } = this.context;

        if (!navigate) {
          throw new Error('Navigation not available');
        }

        const route = typeof payload === 'string' ? payload : payload.route;
        navigate(route);

        return {
          message: `Navigated to ${route}`,
          artifacts: {
            route,
            navigatedAt: new Date().toISOString(),
          },
        };
      },

      // ============================================
      // WORKFLOW AUTOMATION TRIGGERS
      // ============================================

      CLASSIFY_NEAR_MISS: async () => {
        logger.warn('[RealCommandExecutors] Near-miss classification not wired');
        return {
          message: 'Near-miss classification is not wired to production data yet.',
          artifacts: { status: 'not_wired', feature: 'near_miss_classification' },
        };
      },

      RECORD_CLASSIFICATION_FEEDBACK: async () => {
        logger.warn('[RealCommandExecutors] Classification feedback not wired');
        return {
          message: 'Classification feedback is not wired to production data yet.',
          artifacts: { status: 'not_wired', feature: 'classification_feedback' },
        };
      },

      CHECK_ESCALATION_RULES: async () => {
        logger.warn('[RealCommandExecutors] Escalation rules not wired');
        return {
          message: 'Escalation rules are not wired to production data yet.',
          artifacts: { status: 'not_wired', feature: 'escalation_rules' },
        };
      },

      PROCESS_DUE_REMINDERS: async () => {
        logger.warn('[RealCommandExecutors] Scheduled reminders not wired');
        return {
          message: 'Scheduled reminders are not wired to production data yet.',
          artifacts: { status: 'not_wired', feature: 'scheduled_reminders' },
        };
      },

      APPROVE_INTELLIGENT_ACTION: async (payload) => {
        const { actionId, action } = payload;
        const { showNotification, recordLearning } = this.context;

        // Execute the recommended action based on its kind
        let result = { message: 'Action approved', artifacts: {} };

        switch (action?.kind) {
          case 'hazard_pending':
            // Schedule inspection or flag for review
            result = await this.executeCommand('SCHEDULE_EVENT', {
              title: `Review: ${action.title}`,
              eventType: 'Inspection',
              date: 'today',
              time: '10:00 AM',
            });
            break;

          case 'training_overdue':
            // Assign training
            result = await this.executeCommand('ASSIGN_TRAINING', {
              module: action.title,
              workers: action.affectedWorkers || ['all_workers'],
              deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            });
            break;

          case 'risk_spike':
          case 'worker_high_risk':
            // Send safety alert
            result = await this.executeCommand('SEND_NOTIFICATION', {
              recipients: ['safety_team'],
              message: `Action required: ${action.title}`,
              priority: 'high',
            });
            break;

          case 'compliance_drop':
            // Schedule compliance review
            result = await this.executeCommand('SCHEDULE_EVENT', {
              title: `Compliance Review: ${action.title}`,
              eventType: 'Review',
              date: 'tomorrow',
              time: '9:00 AM',
            });
            break;

          default:
            // Generic action handling
            break;
        }

        // Record learning for ML improvement
        if (recordLearning) {
          recordLearning({
            actionId,
            actionKind: action?.kind,
            decision: 'approved',
            timestamp: new Date().toISOString(),
          });
        }

        if (showNotification) {
          showNotification({
            type: 'success',
            message: `Action approved: ${action?.title || actionId}`,
            duration: 4000,
          });
        }

        return {
          message: `Action "${action?.title || actionId}" approved and executed`,
          artifacts: {
            actionId,
            kind: action?.kind,
            decision: 'approved',
            executionResult: result.artifacts,
            approvedAt: new Date().toISOString(),
          },
        };
      },

      DISMISS_INTELLIGENT_ACTION: async (payload) => {
        const { actionId, action, reason } = payload;
        const { showNotification, recordLearning } = this.context;

        // Record learning for ML improvement (dismissal is negative feedback)
        if (recordLearning) {
          recordLearning({
            actionId,
            actionKind: action?.kind,
            decision: 'dismissed',
            reason: reason || 'user_dismissed',
            timestamp: new Date().toISOString(),
          });
        }

        logger.info('🚫 Action dismissed:', {
          actionId,
          kind: action?.kind,
          reason,
        });

        if (showNotification) {
          showNotification({
            type: 'info',
            message: `Action dismissed: ${action?.title || actionId}`,
            duration: 3000,
          });
        }

        return {
          message: `Action "${action?.title || actionId}" dismissed`,
          artifacts: {
            actionId,
            kind: action?.kind,
            decision: 'dismissed',
            reason: reason || 'user_dismissed',
            dismissedAt: new Date().toISOString(),
            learningRecorded: true,
          },
        };
      },

      DEFER_INTELLIGENT_ACTION: async (payload) => {
        const { actionId, action, deferUntil } = payload;
        const { showNotification, addEvent } = this.context;

        // Schedule a reminder to revisit this action
        const deferDate = deferUntil
          ? new Date(deferUntil)
          : new Date(Date.now() + 24 * 60 * 60 * 1000); // Default: 24 hours

        if (addEvent) {
          addEvent({
            id: `DEF_${actionId}_${Date.now()}`,
            title: `Review deferred action: ${action?.title || actionId}`,
            type: 'Reminder',
            date: deferDate.toISOString(),
            metadata: { originalActionId: actionId, actionKind: action?.kind },
            createdBy: 'VH Agent Assistant',
          });
        }

        logger.info('⏰ Action deferred:', {
          actionId,
          kind: action?.kind,
          deferUntil: deferDate.toISOString(),
        });

        if (showNotification) {
          showNotification({
            type: 'info',
            message: `Action deferred until ${deferDate.toLocaleDateString()}`,
            duration: 4000,
          });
        }

        return {
          message: `Action "${action?.title || actionId}" deferred until ${deferDate.toLocaleDateString()}`,
          artifacts: {
            actionId,
            kind: action?.kind,
            decision: 'deferred',
            deferredUntil: deferDate.toISOString(),
            reminderScheduled: true,
          },
        };
      },

      GET_AUTOMATION_STATS: async () => {
        logger.warn('[RealCommandExecutors] Automation stats not wired');
        return {
          message: 'Automation statistics are not wired to production data yet.',
          artifacts: {
            status: 'not_wired',
            feature: 'automation_stats',
            retrievedAt: new Date().toISOString(),
          },
        };
      },
    };

    return executors[command];
  }

  parseDateTime(dateStr, timeStr) {
    const now = new Date();
    let targetDate = new Date();

    // Parse date
    if (dateStr === 'today') {
      // Keep today's date
    } else if (dateStr === 'tomorrow') {
      targetDate.setDate(now.getDate() + 1);
    } else if (dateStr === 'next week') {
      targetDate.setDate(now.getDate() + 7);
    } else if (dateStr.includes('/')) {
      targetDate = new Date(dateStr);
    }

    // Parse time
    if (timeStr) {
      const timeParts = timeStr.match(/(\d+):(\d+)\s*(AM|PM)?/i);
      if (timeParts) {
        let hours = parseInt(timeParts[1]);
        const minutes = parseInt(timeParts[2]);
        const meridiem = timeParts[3];

        if (meridiem) {
          if (meridiem.toUpperCase() === 'PM' && hours !== 12) {
            hours += 12;
          } else if (meridiem.toUpperCase() === 'AM' && hours === 12) {
            hours = 0;
          }
        }

        targetDate.setHours(hours, minutes, 0, 0);
      }
    }

    return targetDate;
  }

  getDefaultDeadline() {
    const deadline = new Date();
    deadline.setDate(deadline.getDate() + 30); // 30 days from now
    return deadline.toISOString();
  }

  hasExecutor(command) {
    const executors = [
      'SCHEDULE_MEETING',
      'CREATE_TOOLBOXTALK',
      'FLAG_INCIDENT',
      'GENERATE_REPORT',
      'SEND_NOTIFICATION',
      'ASSIGN_TRAINING',
      'APPLY_FILTER',
      'NAVIGATE',
      'LOOKUP_EXPLANATION',
      // Workflow automation executors
      'CLASSIFY_NEAR_MISS',
      'RECORD_CLASSIFICATION_FEEDBACK',
      'CHECK_ESCALATION_RULES',
      'PROCESS_DUE_REMINDERS',
      'APPROVE_INTELLIGENT_ACTION',
      'DISMISS_INTELLIGENT_ACTION',
      'DEFER_INTELLIGENT_ACTION',
      'GET_AUTOMATION_STATS',
    ];
    return executors.includes(command);
  }
}

export const realCommandExecutors = new RealCommandExecutors();
